import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  FiArrowRight,
  FiDatabase,
  FiCpu,
  FiShield,
  FiSettings,
  FiKey,
  FiLayers,
  FiHelpCircle,
  FiCheckCircle,
} from "react-icons/fi";
import "./enterprice.css";

const Enterprice = () => {
  // Intersection Observer scroll trigger
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("visible");
          }
        });
      },
      { threshold: 0.1 },
    );

    const animElements = document.querySelectorAll(".animate-on-scroll");
    animElements.forEach((el) => observer.observe(el));

    return () => {
      animElements.forEach((el) => observer.unobserve(el));
    };
  }, []);

  // Form submission simulated state
  const [submitted, setSubmitted] = useState(false);
  const handleContactSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
  };

  const enterpriseFeatures = [
    {
      title: "Data Locality",
      desc: "Keep data on client devices when cloud features aren't required, or configure cloud storage and servers according to organizational requirements.",
      icon: <FiDatabase />,
    },
    {
      title: "On-Premises Deployment",
      desc: "For organizations requiring infrastructure control, LiquidText offers on-premises backend deployment options.",
      icon: <FiCpu />,
    },
    {
      title: "Security & Privacy",
      desc: "LiquidText Enterprise supports password-based file encryption, GDPR compliance, and states that it is ISO 27001:2022 certified.",
      icon: <FiShield />,
    },
    {
      title: "Centralized Management",
      desc: "Manage licenses, users, and deployments through enterprise management capabilities.",
      icon: <FiSettings />,
    },
    {
      title: "Single Sign-On (SSO)",
      desc: "Full support for Single Sign-On (SAML / OIDC) and Active Directory integration.",
      icon: <FiKey />,
    },
    {
      title: "Enterprise Integrations",
      desc: "Integration options are available for document management and security systems.",
      icon: <FiLayers />,
    },
    {
      title: "Dedicated Support",
      desc: "Enterprise customers can receive dedicated support, onboarding, training, and ongoing updates.",
      icon: <FiHelpCircle />,
    },
  ];
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "instant", // or "smooth"
    });
  }, [pathname]);

  return (
    <div className="enterprise-page-wrapper">
      {/* ==================== 1. HERO SECTION ==================== */}
      <section className="enterprise-hero">
        <div className="container hero-grid">
          <div className="hero-text animate-on-scroll fade-up">
            <span className="badge-pill">ENTERPRISE SOLUTiONS</span>
            <h1>
              LiquidText for <br />
              <span className="highlight-text">Organizations</span>
            </h1>
            <p className="hero-desc">
              Bring powerful document analysis and connected research workflows
              to your organization. Designed for organizations that require
              control, security, manageability, and flexible deployment options.
            </p>
            <div className="hero-btn-group">
              <a href="#contact-sales" className="btn-primary">
                Contact Sales <FiArrowRight className="btn-icon" />
              </a>
              <a href="#features" className="btn-secondary">
                View Specifications
              </a>
            </div>
          </div>

          <div className="hero-visual animate-on-scroll fade-left">
            <div className="enterprise-visual-card shadow-lg">
              <img
                src="/images/hero_workspace.png"
                alt="LiquidText Enterprise Connected Research Workspace"
                className="enterprise-visual-img"
              />
              <div className="secure-badge">
                <span className="secure-dot-pulse"></span>
                <span>ISO 27001 Certified</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ==================== 2. GRID FEATURES SECTION ==================== */}
      <section
        id="features"
        className="enterprise-features-grid-section bg-light"
      >
        <div className="container">
          <div className="section-header text-center animate-on-scroll fade-up">
            <span className="section-tag-light">CORE CAPABILITIES</span>
            <h2>Enterprise Features</h2>
            <p className="section-desc max-600">
              Powerful admin controls, customizable deployments, and
              gold-standard encryption built for compliance.
            </p>
          </div>

          <div className="enterprise-features-grid">
            {enterpriseFeatures.map((feat, idx) => (
              <div
                key={idx}
                className="ent-feature-card animate-on-scroll fade-up"
              >
                <div className="ent-feature-icon-wrapper">{feat.icon}</div>
                <h3>{feat.title}</h3>
                <p>{feat.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ==================== 3. SPLIT INFORMATION CARDS ==================== */}

      {/* Infrastructure Control */}
      <section className="enterprise-split-section">
        <div className="container grid-two-col">
          <div className="split-visual animate-on-scroll fade-right">
            <div className="enterprise-visual-card shadow-lg">
              <img
                src="/images/liquidtext_active.png"
                alt="On-Premises deployment document security"
                className="enterprise-visual-img"
              />
            </div>
          </div>

          <div className="split-text animate-on-scroll fade-left">
            <span className="section-tag">CONTROL & COMPLIANCE</span>
            <h2>On-Premises & Data Locality Control</h2>
            <p className="feature-desc">
              For organizations requiring complete infrastructure control,
              LiquidText offers on-premises backend deployment options.
              Configure cloud servers and storage according to your specific
              institutional rules or keep documents completely on client
              devices.
            </p>
            <div className="compliance-checklist">
              <div className="check-item">
                <FiCheckCircle className="check-icon-green" />
                <span>GDPR Compliant architecture</span>
              </div>
              <div className="check-item">
                <FiCheckCircle className="check-icon-green" />
                <span>Password-based file encryption keys</span>
              </div>
              <div className="check-item">
                <FiCheckCircle className="check-icon-green" />
                <span>ISO 27001:2022 certified systems</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Manageability */}
      <section className="enterprise-split-section bg-light">
        <div className="container grid-two-col reverse-col">
          <div className="split-text animate-on-scroll fade-right">
            <span className="section-tag-light">ADMINISTRATIVE CONTROLS</span>
            <h2>Centralized License & User Control</h2>
            <p className="feature-desc">
              Manage licenses, users, and deployment models effortlessly through
              centralized corporate admin capabilities. Integrate with Active
              Directory and configure SAML-based Single Sign-On (SSO) to enforce
              security standards automatically.
            </p>
            <div className="compliance-checklist">
              <div className="check-item">
                <FiCheckCircle className="check-icon-green" />
                <span>SSO & Active Directory integration</span>
              </div>
              <div className="check-item">
                <FiCheckCircle className="check-icon-green" />
                <span>Centralized admin license allocations</span>
              </div>
              <div className="check-item">
                <FiCheckCircle className="check-icon-green" />
                <span>DMS (Document Management System) APIs</span>
              </div>
            </div>
          </div>

          <div className="split-visual animate-on-scroll fade-left">
            <div className="enterprise-visual-card shadow-lg">
              <img
                src="/images/work_across_devices.png"
                alt="License management across Mac, Windows and iOS devices"
                className="enterprise-visual-img"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ==================== 4. CONTACT & SALES FORM ==================== */}
      <section id="contact-sales" className="enterprise-contact-section">
        <div className="container contact-grid">
          <div className="contact-info animate-on-scroll fade-right">
            <span className="section-tag">GET IN TOUCH</span>
            <h2>Partner With Us</h2>
            <p className="contact-desc">
              Our enterprise advisors are ready to help you set up flexible
              deployment models, custom storage servers, SSO integrations, and
              coordinate dedicated team training.
            </p>
            <div className="contact-bullet-points">
              <div className="bullet-card-light">
                <h4>🤝 Dedicated Support</h4>
                <p>
                  Enterprise onboarding, staff training, and ongoing prioritised
                  updates.
                </p>
              </div>
              <div className="bullet-card-light">
                <h4>📄 custom Integration Specs</h4>
                <p>
                  Learn how to connect our workspaces with your current document
                  security frameworks.
                </p>
              </div>
            </div>
          </div>

          <div className="contact-form-wrapper animate-on-scroll fade-left">
            <div className="form-card shadow-lg">
              {submitted ? (
                <div className="form-success text-center">
                  <div className="success-icon-badge">✓</div>
                  <h3>Message Sent Successfully!</h3>
                  <p>
                    Our corporate team will reach out to you within 24 business
                    hours.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleContactSubmit}>
                  <h3>Request Enterprise Consultation</h3>
                  <div className="form-group">
                    <label>Full Name</label>
                    <input type="text" placeholder="John Doe" required />
                  </div>
                  <div className="form-group">
                    <label>Work Email</label>
                    <input
                      type="email"
                      placeholder="john@organization.com"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Organization Name</label>
                    <input type="text" placeholder="Acme Corp" required />
                  </div>
                  <div className="form-group">
                    <label>Brief Message</label>
                    <textarea
                      placeholder="Tell us about your team's deployment and security requirements..."
                      rows="3"
                      required
                    ></textarea>
                  </div>
                  <button type="submit" className="btn-form-submit">
                    Send Consult Request
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Enterprice;
